import { Fragment, useMemo, useState } from "react"
import type { ReactNode } from "react"
import { CaretDown, CaretRight, Warning } from "@phosphor-icons/react"

import { useForecasts } from "~/hooks/useForecasts"
import { usePrimaryWarehouses } from "~/hooks/useWarehouses"
import { usePreorderShortfallRows, type StockWalkEvent } from "~/hooks/usePreorderShortfallRows"
import { formatDate, startOfTodayUTC } from "~/utils/datetime"
import { formatNumber } from "~/utils/formatting"
import { formatName } from "~/utils/namingUtils"
import {
  getAtRiskDescriptionShort,
  getAtRiskDescriptionLong,
  getUnfulfillableDescriptionLong,
  getUnfulfillableDescriptionShort,
  getTimingLabel,
  makeRegularDemandFn,
  type ForecastDemandFn,
} from "~/utils/unfulfillablePreorderUtils"
import { Tooltip } from "../Tooltip"
import InfoTooltip from "../InfoTooltip"
import { cn } from "~/utils/helpers"

import {
  DialogBodyV2,
  DialogContentV2,
  DialogHeaderV2,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "../Dialog"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../Table"

type Props = {
  variantIds: string[]
  skuIds?: string[]
  /** When provided, only preorders with deliveryDate within this range are shown */
  dateRange?: { start: Date; end: Date }
  /** Controlled mode — omit to use trigger-based (children) mode */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  children?: ReactNode
}

type TimelineMarker = { date: Date; label: string }

type WalkRow =
  | { kind: "demand"; date: Date; amount: number; balance: number }
  | { kind: "incoming"; date: Date; quantity: number; balance: number }
  | {
      kind: "preorder"
      date: Date
      channel: string
      quantity: number
      unfulfillableQty: number
      atRiskQty: number
      balance: number
    }
  | { kind: "marker"; date: Date; label: string }

// Single projected-stock walk: per interval, subtract forecast demand (balance floors at
// zero — a stockout loses sales, not stock), then apply incoming (+) and pre-orders (-). A
// pre-order's displayed balance goes negative by its shortfall — the authoritative
// unfulfillable/at-risk scalars, not the raw running balance — so it reconciles with the table.
function buildWalkRows(
  events: StockWalkEvent[],
  today: Date,
  initialStock: number,
  getForecastDemand: ForecastDemandFn,
  markers: TimelineMarker[],
): WalkRow[] {
  const rows: WalkRow[] = []
  let balance = initialStock
  let lastDate = today
  for (const event of events) {
    const before = balance
    balance = Math.max(
      0,
      balance - getForecastDemand(event.skuId, event.warehouseId, lastDate, event.date),
    )
    lastDate = event.date
    const demand = before - balance
    if (demand > 0.5) {
      rows.push({ kind: "demand", date: event.date, amount: demand, balance })
    }
    if (event.type === "incoming") {
      balance += event.quantity
      rows.push({ kind: "incoming", date: event.date, quantity: event.quantity, balance })
    } else {
      const shortfall = event.unfulfillableQty + event.atRiskQty
      const displayBalance = shortfall > 0 ? -shortfall : Math.max(0, balance - event.quantity)
      rows.push({
        kind: "preorder",
        date: event.date,
        channel: event.channel,
        quantity: event.quantity,
        unfulfillableQty: event.unfulfillableQty,
        atRiskQty: event.atRiskQty,
        balance: displayBalance,
      })
      balance = Math.max(0, balance - event.quantity)
    }
  }
  for (const m of markers) {
    rows.push({ kind: "marker", date: m.date, label: m.label })
  }
  // Stable sort by date keeps event rows (pushed first) ahead of same-date markers.
  return rows.sort((a, b) => a.date.getTime() - b.date.getTime())
}

type StockWalkTimelineProps = {
  skuId: string
  warehouseId: string
  sortedEvents: StockWalkEvent[]
  initialStock: number
  colCount: number
  today: Date
  getForecastDemand: ForecastDemandFn
  leadTimeEndDate: Date
  purchasePeriodEndDate: Date
  // Walk only up to the pre-order being explained; later events don't affect its shortfall.
  truncateDate: Date
}

function StockWalkTimeline({
  skuId,
  warehouseId,
  sortedEvents,
  initialStock,
  colCount,
  today,
  getForecastDemand,
  leadTimeEndDate,
  purchasePeriodEndDate,
  truncateDate,
}: StockWalkTimelineProps) {
  const rows = useMemo(() => {
    const events = sortedEvents.filter(
      (e) =>
        e.skuId === skuId &&
        e.warehouseId === warehouseId &&
        e.date.getTime() <= truncateDate.getTime(),
    )
    const markers: TimelineMarker[] = [
      { date: leadTimeEndDate, label: "Lead time ends" },
      { date: purchasePeriodEndDate, label: "Purchase period ends" },
    ].filter((m) => m.date.getTime() <= truncateDate.getTime())
    return buildWalkRows(events, today, initialStock, getForecastDemand, markers)
  }, [
    sortedEvents,
    skuId,
    warehouseId,
    initialStock,
    today,
    getForecastDemand,
    leadTimeEndDate,
    purchasePeriodEndDate,
    truncateDate,
  ])

  return (
    <TableRow>
      <TableCell colSpan={colCount} className="bg-subtle p-0">
        <div className="px-5 py-3">
          <div className="text-xs text-fg-subtle mb-2 font-medium">Stock walk for {skuId}</div>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-fg-subtle border-b border-base">
                <th className="text-left pb-1.5 pr-4 font-medium">Date</th>
                <th className="text-left pb-1.5 pr-4 font-medium">Event</th>
                <th className="text-right pb-1.5 pr-4 font-medium">Change</th>
                <th className="text-right pb-1.5 font-medium">
                  <Tooltip
                    title="Projected stock after forecasted demand; negative = shortfall"
                    side="top"
                    disableHoverableContent
                  >
                    <span>Balance</span>
                  </Tooltip>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className="text-fg-subtle border-b border-base">
                <td className="pr-4 py-1">Today</td>
                <td className="pr-4 py-1 italic">Current stock</td>
                <td className="text-right pr-4 py-1 tabular-nums">—</td>
                <td className="text-right py-1 tabular-nums font-semibold text-fg-base">
                  {formatNumber(initialStock)}
                </td>
              </tr>
              {rows.map((row, i) => {
                if (row.kind === "marker") {
                  return (
                    <tr key={`marker|${row.label}|${i}`} className="text-fg-subtle">
                      <td className="pr-4 pt-2 pb-1 border-t border-dashed border-base whitespace-nowrap">
                        {formatDate(row.date)}
                      </td>
                      <td
                        className="pt-2 pb-1 border-t border-dashed border-base italic"
                        colSpan={3}
                      >
                        {row.label}
                      </td>
                    </tr>
                  )
                }
                if (row.kind === "demand") {
                  return (
                    <tr key={`demand|${row.date.getTime()}|${i}`} className="text-icon-orange">
                      <td className="pr-4 py-1">{formatDate(row.date)}</td>
                      <td className="pr-4 py-1 italic">Forecast demand</td>
                      <td className="text-right pr-4 py-1 tabular-nums">
                        -{formatNumber(row.amount)}
                      </td>
                      <td className="text-right py-1 tabular-nums font-semibold text-fg-base">
                        {formatNumber(row.balance)}
                      </td>
                    </tr>
                  )
                }
                if (row.kind === "incoming") {
                  return (
                    <tr key={`incoming|${row.date.getTime()}|${i}`} className="text-fg-base">
                      <td className="pr-4 py-1">{formatDate(row.date)}</td>
                      <td className="pr-4 py-1 text-fg-subtle font-medium">Incoming stock</td>
                      <td className="text-right pr-4 py-1 tabular-nums font-medium text-green-600">
                        +{formatNumber(row.quantity)}
                      </td>
                      <td className="text-right py-1 tabular-nums font-semibold text-fg-base">
                        {formatNumber(row.balance)}
                      </td>
                    </tr>
                  )
                }
                const isRed = row.unfulfillableQty > 0
                const isOrange = !isRed && row.atRiskQty > 0
                return (
                  <tr
                    key={`preorder|${row.date.getTime()}|${i}`}
                    className={
                      isRed ? "text-icon-red" : isOrange ? "text-icon-orange" : "text-fg-base"
                    }
                  >
                    <td className="pr-4 py-1">{formatDate(row.date)}</td>
                    <td className="pr-4 py-1 font-medium">
                      {row.channel} {formatName("preorder")}{" "}
                      <span className="text-fg-subtle font-normal">
                        ({formatNumber(row.quantity)} units)
                      </span>
                    </td>
                    <td className="text-right pr-4 py-1 tabular-nums">
                      -{formatNumber(row.quantity)}
                    </td>
                    <td className="text-right py-1 tabular-nums font-semibold">
                      {formatNumber(row.balance)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </TableCell>
    </TableRow>
  )
}

function PreorderWhyDialogContent({ variantIds, skuIds, dateRange }: Omit<Props, "children">) {
  const primaryWarehouses = usePrimaryWarehouses()
  const { shortfallRows, skuStockMap, sortedEvents } = usePreorderShortfallRows(variantIds, {
    skuIds,
    dateRange,
  })
  const [expandedRow, setExpandedRow] = useState<string | null>(null)
  const today = useMemo(() => startOfTodayUTC(), [])
  const { getSkuForecastsBetweenDates } = useForecasts()
  // Forecast bakes known pre-orders in at their delivery week and the walk subtracts them
  // again as explicit events, so exclude them here (makeRegularDemandFn) — demand rows then
  // show only regular sales pressure, keeping the balance consistent with the at-risk figure.
  const getForecastDemand = useMemo<ForecastDemandFn>(() => {
    const knownPreorders = sortedEvents
      .filter((e) => e.type === "preorder")
      .map((e) => ({
        skuId: e.skuId,
        warehouseId: e.warehouseId,
        deliveryDate: e.date,
        quantity: e.quantity,
      }))
    return makeRegularDemandFn(
      (skuId, wh, from, to) => getSkuForecastsBetweenDates(skuId, [wh], from, to),
      knownPreorders,
    )
  }, [getSkuForecastsBetweenDates, sortedEvents])

  const colCount = primaryWarehouses.length > 1 ? 9 : 8

  // Precompute group index per row so alternating background doesn't require O(n²) work
  const skuGroupIndex = useMemo(() => {
    const map = new Map<string, number>()
    let group = 0
    let prevSkuId: string | undefined
    for (const po of shortfallRows) {
      if (prevSkuId !== undefined && po.skuId !== prevSkuId) group++
      map.set(`${po.skuId}|${po.warehouseId}|${po.channel}|${po.deliveryDate.getTime()}`, group)
      prevSkuId = po.skuId
    }
    return map
  }, [shortfallRows])

  return (
    <>
      <div className="space-y-1.5 text-sm px-5 py-2">
        <div className="flex items-start gap-2">
          <Warning weight="fill" className="text-icon-red mt-0.5 shrink-0" size={14} />
          <p>
            <span className="font-semibold text-icon-red">
              {formatName("unfulfillable_preorder", true, true)}:
            </span>{" "}
            {getUnfulfillableDescriptionShort()}
          </p>
          <InfoTooltip
            className="h-4 w-4 shrink-0 mt-0.5"
            title={getUnfulfillableDescriptionLong()}
          />
        </div>
        <div className="flex items-start gap-2">
          <Warning weight="fill" className="text-icon-orange mt-0.5 shrink-0" size={14} />
          <p>
            <span className="font-semibold text-icon-orange">
              {formatName("at_risk_preorder", true, true)}:
            </span>{" "}
            {getAtRiskDescriptionShort()}
          </p>
          <InfoTooltip className="h-4 w-4 shrink-0 mt-0.5" title={getAtRiskDescriptionLong()} />
        </div>
      </div>

      {shortfallRows.length > 0 && (
        <div className="rounded-[8px] border border-base overflow-hidden mx-5 mb-4">
          <div className="overflow-auto max-h-[50vh]">
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-subtle">
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead>SKU ID</TableHead>
                  <TableHead>Timing</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Stock now</TableHead>
                  <TableHead className="text-right text-icon-red">Unfulfillable</TableHead>
                  <TableHead className="text-right text-icon-orange">
                    <Tooltip title={getAtRiskDescriptionShort()} side="top" disableHoverableContent>
                      <span>At-risk</span>
                    </Tooltip>
                  </TableHead>
                  <TableHead>Delivery date</TableHead>
                  {primaryWarehouses.length > 1 && <TableHead>Warehouse</TableHead>}
                  <TableHead>Channel</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {shortfallRows.map((po) => {
                  const timingLabel = getTimingLabel(po.timing)
                  const currentStock = skuStockMap.get(`${po.skuId}|${po.warehouseId}`) ?? 0
                  const rowKey = `${po.skuId}|${po.warehouseId}|${po.channel}|${po.deliveryDate.getTime()}`
                  const isExpanded = expandedRow === rowKey
                  const isEvenGroup = (skuGroupIndex.get(rowKey) ?? 0) % 2 === 0
                  return (
                    <Fragment key={rowKey}>
                      <TableRow
                        className={cn(
                          "cursor-pointer",
                          !isEvenGroup && "bg-subtle hover:bg-subtle-hover",
                        )}
                        onClick={() => setExpandedRow(isExpanded ? null : rowKey)}
                      >
                        <TableCell className="w-8 text-fg-subtle">
                          {isExpanded ? <CaretDown size={12} /> : <CaretRight size={12} />}
                        </TableCell>
                        <TableCell>{po.skuId}</TableCell>
                        <TableCell className="text-muted-foreground">{timingLabel}</TableCell>
                        <TableCell className="text-right tabular-nums">{po.quantity}</TableCell>
                        <TableCell className="text-right tabular-nums text-fg-subtle">
                          {formatNumber(currentStock)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-medium text-icon-red">
                          {po.unfulfillableQty > 0 ? formatNumber(po.unfulfillableQty) : "—"}
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-medium text-icon-orange">
                          {po.atRiskQty > 0 ? formatNumber(po.atRiskQty) : "—"}
                        </TableCell>
                        <TableCell>{formatDate(po.deliveryDate)}</TableCell>
                        {primaryWarehouses.length > 1 && <TableCell>{po.warehouseId}</TableCell>}
                        <TableCell>{po.channel}</TableCell>
                      </TableRow>
                      {isExpanded && (
                        <StockWalkTimeline
                          skuId={po.skuId}
                          warehouseId={po.warehouseId}
                          sortedEvents={sortedEvents}
                          initialStock={currentStock}
                          colCount={colCount}
                          today={today}
                          getForecastDemand={getForecastDemand}
                          leadTimeEndDate={po.boundaries.leadTimeEnd}
                          purchasePeriodEndDate={po.boundaries.purchasePeriodEnd}
                          truncateDate={po.deliveryDate}
                        />
                      )}
                    </Fragment>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {shortfallRows.length === 0 && (
        <p className="text-sm text-fg-subtle px-5 pb-4">
          No unfulfillable or at-risk {formatName("preorder", true)} for this period.
        </p>
      )}
    </>
  )
}

export default function PreorderWhyDialog({
  variantIds,
  skuIds,
  dateRange,
  open,
  onOpenChange,
  children,
}: Props) {
  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}
      <DialogContentV2 className="max-w-3xl">
        <DialogHeaderV2>
          <DialogTitle>{formatName("preorder", true, true)} Fulfillment</DialogTitle>
        </DialogHeaderV2>
        <DialogBodyV2 className="overflow-visible p-0 gap-0">
          {open !== false && (
            <PreorderWhyDialogContent
              variantIds={variantIds}
              skuIds={skuIds}
              dateRange={dateRange}
            />
          )}
        </DialogBodyV2>
      </DialogContentV2>
    </DialogRoot>
  )
}
